// Utility functions extracted from HomePage.js

export const formatDate = (dateStr) => {
  try {
    const date = new Date(dateStr);
    const day = date.getDate();
    const month = date.toLocaleString('default', { month: 'long' });
    const year = date.getFullYear();
    const suffix = (day === 1 || day === 21 || day === 31) ? 'st' 
      : (day === 2 || day === 22) ? 'nd' 
      : (day === 3 || day === 23) ? 'rd' : 'th';
    return `${day}${suffix} ${month}, ${year}`;
  } catch {
    return dateStr;
  }
};

export const getUserAvatar = (name, isOrange = false) => {
  const names = name.split(' ');
  const initials = names.map(n => n[0]).join('').toUpperCase().slice(0, 2);
  const colors = ['#52B8AC', '#E8795E', '#F5A623', '#DC2626', '#667eea'];
  const color = isOrange ? '#F5A623' : colors[name.charCodeAt(0) % colors.length];
  return { initials, color };
};

export const calculateProgress = (challenge) => {
  if (!challenge.participants || challenge.participants.length === 0) return 0;
  const targetParticipants = challenge.target_participants || 10;
  const currentParticipants = challenge.participants.length;
  return Math.min(Math.round((currentParticipants / targetParticipants) * 100), 100);
};

export const getProgressColor = (progress) => {
  if (progress >= 90) return '#10B981';
  if (progress >= 70) return '#F59E0B';
  if (progress >= 50) return '#EF4444';
  return '#6B7280';
};

export const getChallengeCategory = (challenge) => {
  const title = challenge.title.toLowerCase();
  if (title.includes('walk') || title.includes('run') || title.includes('exercise')) return 'fitness';
  if (title.includes('meditat') || title.includes('mindful') || title.includes('zen')) return 'meditation';
  if (title.includes('eco') || title.includes('green')) return 'eco';
  return 'general';
};

// Derive category for MAD cards from title keywords
export const getCategory = (title, existingCategory) => {
  if (existingCategory && existingCategory !== 'N/A' && existingCategory !== 'None') {
    return existingCategory.charAt(0).toUpperCase() + existingCategory.slice(1);
  }
  const t = (title || '').toLowerCase();
  if (t.includes('hik') || t.includes('fitness') || t.includes('yoga') || t.includes('meditation') || t.includes('step') || t.includes('walk') || t.includes('gym') || t.includes('exercise') || t.includes('health') || t.includes('mindful')) return 'Wellness';
  if (t.includes('wheelchair') || t.includes('inclus') || t.includes('divers') || t.includes('access') || t.includes('mentor') || t.includes('sign language')) return 'Inclusion';
  if (t.includes('cv') || t.includes('career') || t.includes('skill') || t.includes('learn') || t.includes('workshop') || t.includes('resume') || t.includes('shadow')) return 'Career Dev';
  if (t.includes('volunt') || t.includes('communit') || t.includes('donat') || t.includes('charit') || t.includes('clean-up') || t.includes('cleanup') || t.includes('blood') || t.includes('teach') || t.includes('digital skills')) return 'Community';
  if (t.includes('climat') || t.includes('green') || t.includes('sustain') || t.includes('eco') || t.includes('plant') || t.includes('recycle') || t.includes('plastic') || t.includes('commute') || t.includes('saplings')) return 'Climate';
  return 'Wellness';
};

// Image selection based on challenge title/category
export const getImageForChallenge = (challenge) => {
  if (challenge?.photo_tag) return challenge.photo_tag;
  if (challenge?.image_url) return challenge.image_url;
  const t = (challenge?.title || '').toLowerCase();
  const c = (challenge?.category || '').toLowerCase();

  if (t.includes('park clean') || t.includes('cleanup') || t.includes('clean-up')) return 'https://images.pexels.com/photos/8543032/pexels-photo-8543032.jpeg?auto=compress&w=800';
  if (t.includes('blood') || t.includes('donation')) return 'https://images.pexels.com/photos/7581110/pexels-photo-7581110.jpeg?auto=compress&w=800';
  if (t.includes('plant') || t.includes('sapling') || t.includes('tree')) return 'https://images.pexels.com/photos/8543032/pexels-photo-8543032.jpeg?auto=compress&w=800';
  if (t.includes('digital skills') || t.includes('teach') || t.includes('computer')) return 'https://images.unsplash.com/photo-1573166364839-1bfe9196c23e?w=800';
  if (t.includes('yoga')) return 'https://customer-assets.emergentagent.com/job_92851dff-cc0d-44f9-97e7-b10a3a8bd28c/artifacts/5hmlgwwr_d353ec47-f57a-4716-9992-34e0719a686d.jpg';
  if (t.includes('meditation') || t.includes('mindful')) return 'https://images.unsplash.com/photo-1599447421376-611783057464?w=800';
  if (t.includes('step') || t.includes('walk') || t.includes('10k')) return 'https://images.unsplash.com/photo-1717500251894-e127a481632e?w=800';
  if (t.includes('plastic') || t.includes('recycle')) return 'https://images.pexels.com/photos/8543032/pexels-photo-8543032.jpeg?auto=compress&w=800';
  if (t.includes('commute') || t.includes('carpool') || t.includes('bike')) return 'https://images.pexels.com/photos/7580644/pexels-photo-7580644.jpeg?auto=compress&w=800';
  if (t.includes('access') || t.includes('inclus') || t.includes('wheelchair')) return 'https://images.pexels.com/photos/7581114/pexels-photo-7581114.jpeg?auto=compress&w=800';
  if (t.includes('mentor')) return 'https://images.unsplash.com/photo-1628495073765-e7fdbb7c25fc?w=800';
  if (t.includes('sign language')) return 'https://images.pexels.com/photos/7581110/pexels-photo-7581110.jpeg?auto=compress&w=800';
  if (t.includes('skill') || t.includes('lunch session')) return 'https://images.unsplash.com/photo-1769740333462-9a63bfa914bc?w=800';
  if (t.includes('resume') || t.includes('cv')) return 'https://images.unsplash.com/photo-1764690690771-b4522d66b433?w=800';
  if (t.includes('shadow') || t.includes('department')) return 'https://images.unsplash.com/photo-1716703373020-17ff360924ee?w=800';

  if (c.includes('wellness')) return 'https://customer-assets.emergentagent.com/job_92851dff-cc0d-44f9-97e7-b10a3a8bd28c/artifacts/5hmlgwwr_d353ec47-f57a-4716-9992-34e0719a686d.jpg';
  if (c.includes('inclusion')) return 'https://images.pexels.com/photos/7581114/pexels-photo-7581114.jpeg?auto=compress&w=800';
  if (c.includes('career')) return 'https://images.unsplash.com/photo-1769740333462-9a63bfa914bc?w=800';
  if (c.includes('community')) return 'https://images.pexels.com/photos/7581110/pexels-photo-7581110.jpeg?auto=compress&w=800';
  if (c.includes('climate')) return 'https://images.pexels.com/photos/8543032/pexels-photo-8543032.jpeg?auto=compress&w=800';

  return 'https://images.unsplash.com/photo-1573166364839-1bfe9196c23e?w=800';
};

// Category & scope style maps
export const categoryStyles = {
  'Wellness': { bg: '#B4E1D8', color: '#2C6A5F' },
  'Inclusion': { bg: '#FBC98A', color: '#8B5A2B' },
  'Career Dev': { bg: '#73C8B9', color: '#1A4A42' },
  'Community': { bg: '#F8AD7B', color: '#7A4420' },
  'Climate': { bg: '#5DBFAD', color: '#FFFFFF' }
};

export const scopeStyles = {
  'solo': { bg: '#B4E1D8', color: '#2C6A5F', label: 'Solo' },
  'individual': { bg: '#B4E1D8', color: '#2C6A5F', label: 'Solo' },
  'team': { bg: '#73C8B9', color: '#1A4A42', label: 'Team' },
  'company': { bg: '#F8AD7B', color: '#7A4420', label: 'Company' },
  'community': { bg: '#F8AD7B', color: '#7A4420', label: 'Company' }
};

// Theme colors
export const TEAL_ACCENT = '#5DBFAD';
export const TEAL_MUTED = '#7FCABC';
export const ORANGE_ACCENT = '#F8AD7B';
export const ORANGE_MUTED = '#F5BC95';
